import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import CollapsibleSection from '../CollapsibleSection'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  copyTextToClipboard,
  formatDash,
  formatTransferType,
  getAdminPaymentProofDetailFields,
  getShwapnoReceivingAccountLabel,
  isPaymentProofFileImage,
  isPaymentProofFilePdf,
} from '../../utils/paymentProofDisplay'

function formatPaymentProofDateTime(value) {
  if (value == null || value === '') return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function formatDate(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return '—'
  }
}

function formatAmount(value) {
  const n = Number(value ?? 0)
  return `৳${n.toFixed(2)}`
}

function statusChip(status) {
  const upper = String(status || '').toUpperCase()
  if (upper === 'VERIFIED') return { label: 'Verified', color: 'success' }
  if (upper === 'REJECTED') return { label: 'Rejected', color: 'error' }
  if (upper === 'PENDING') return { label: 'Pending', color: 'warning' }
  return { label: status || '—', color: 'default' }
}

function CopyableValue({ label, value }) {
  const [copied, setCopied] = useState(false)
  const display = formatDash(value)
  const canCopy = display !== '—'
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{display}</Typography>
        {canCopy ? (
          <Tooltip title={copied ? 'Copied' : 'Copy'}>
            <IconButton
              size="small"
              onClick={async () => {
                const ok = await copyTextToClipboard(display)
                if (ok) {
                  setCopied(true)
                  setTimeout(() => setCopied(false), 1500)
                }
              }}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  )
}

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDash(value)}</Typography>
    </Box>
  )
}

function LegacyProofCard({ file, onPreview }) {
  const fileUrl = file.file_url
  const isImage = isPaymentProofFileImage(fileUrl)
  const isPdf = isPaymentProofFilePdf(fileUrl)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', md: 120 },
          height: 120,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid #eee',
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: fileUrl ? 'pointer' : 'default',
        }}
        onClick={() => fileUrl && onPreview(fileUrl, `payment-proof-${file.id}`)}
      >
        {isImage ? (
          <Box
            component="img"
            src={fileUrl}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {isPdf ? 'PDF' : 'File'}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Chip label="Earlier upload" size="small" variant="outlined" sx={{ mb: 1.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This file was saved before amount, bank, and bKash details were collected.
        </Typography>
        <DetailField
          label="Payment date & time"
          value={formatPaymentProofDateTime(file.payment_at)}
        />
      </Box>
    </Box>
  )
}

export default function PaymentProofEntriesSection({
  proofs = [],
  legacyFiles = [],
  bankAccountBySettingsId = {},
  canAddFile,
  onAddFile,
  onPreview,
  uploadFeedback,
  onDismissUploadFeedback,
  onExpandedChange,
  sectionStatus = 'ready',
  sectionError = null,
  onRetrySection,
}) {
  const addFileButton = canAddFile ? (
    <Button
      variant="outlined"
      size="small"
      startIcon={<CloudUploadIcon />}
      onClick={onAddFile}
    >
      Add file
    </Button>
  ) : null

  const hasLegacy = legacyFiles.length > 0

  if (sectionStatus !== 'ready') {
    return (
      <CollapsibleSection title="Payment Proof" onExpandedChange={onExpandedChange}>
        {sectionStatus === 'error' ? (
          <Alert
            severity="error"
            action={
              onRetrySection ? (
                <Button color="inherit" size="small" onClick={onRetrySection}>
                  Retry
                </Button>
              ) : null
            }
          >
            {sectionError || 'Failed to load this section'}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </CollapsibleSection>
    )
  }

  if (!proofs.length && !hasLegacy && !canAddFile) {
    return (
      <CollapsibleSection title="Payment Proof" onExpandedChange={onExpandedChange}>
        <Typography variant="body2" color="text.secondary">
          No payment proofs on file yet.
        </Typography>
      </CollapsibleSection>
    )
  }

  return (
    <CollapsibleSection
      title="Payment Proof"
      headerExtra={addFileButton}
      onExpandedChange={onExpandedChange}
    >
      {!canAddFile && proofs.length > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Files can only be added while the order is Accepted and your role includes procurement
          access.
        </Typography>
      ) : null}
      {uploadFeedback ? (
        <Alert severity={uploadFeedback.severity} sx={{ mb: 2 }} onClose={onDismissUploadFeedback}>
          {uploadFeedback.message}
        </Alert>
      ) : null}
      {proofs.length === 0 && !hasLegacy ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No structured payment proofs on file yet. Use Add file to upload.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {proofs.map((proof, index) => {
              const fileUrl = proof.file_url
              const isPending =
                String(proof.verification_status || '').toUpperCase() === 'PENDING'
              const chip = statusChip(proof.verification_status)
              const isImage = isPaymentProofFileImage(fileUrl)
              const isPdf = isPaymentProofFilePdf(fileUrl)
              return (
                <Box key={proof.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isPending ? '#fcd34d' : '#e5e7eb',
                      bgcolor: isPending ? 'rgba(255, 251, 235, 0.6)' : '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: '100%', md: 120 },
                        height: 120,
                        flexShrink: 0,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #eee',
                        bgcolor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: fileUrl ? 'pointer' : 'default',
                      }}
                      onClick={() => fileUrl && onPreview(fileUrl, `payment-proof-${proof.id}`)}
                    >
                      {isImage ? (
                        <Box
                          component="img"
                          src={fileUrl}
                          alt=""
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {isPdf ? 'PDF' : 'File'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                        <Chip label={formatTransferType(proof.payment_method)} size="small" variant="outlined" />
                        <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />
                        <Typography variant="body2" sx={{ fontWeight: 600, alignSelf: 'center' }}>
                          {formatAmount(proof.requested_amount)}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailField label="Amount paid" value={formatAmount(proof.requested_amount)} />
                        </Grid>
                        {getAdminPaymentProofDetailFields(proof).map((field) => (
                          <Grid item xs={12} sm={6} md={4} key={field.key}>
                            {field.copyable ? (
                              <CopyableValue label={field.label} value={field.value} />
                            ) : (
                              <DetailField label={field.label} value={field.value} />
                            )}
                          </Grid>
                        ))}
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailField
                            label="Payment date & time"
                            value={formatPaymentProofDateTime(proof.payment_at)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailField label="Upload time" value={formatDate(proof.created_at)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <DetailField label="Request ID" value={proof.request_id ? `#${proof.request_id}` : null} />
                        </Grid>
                        <Grid item xs={12}>
                          <DetailField
                            label="Shwapno receiving account"
                            value={getShwapnoReceivingAccountLabel(proof, bankAccountBySettingsId)}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                  {index < proofs.length - 1 ? <Divider sx={{ mt: 2 }} /> : null}
                </Box>
              )
            })}
        </Box>
      )}
      {hasLegacy ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: proofs.length ? 2 : 0 }}>
          {proofs.length > 0 ? <Divider /> : null}
          {legacyFiles.map((file) => (
            <LegacyProofCard key={file.id} file={file} onPreview={onPreview} />
          ))}
        </Box>
      ) : null}
    </CollapsibleSection>
  )
}
